import { Box, InputLabel, Slider, Stack, Typography } from '@mui/material';
import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import HelpPopover from '../../../HelpPopover';
import { updateParameter } from '../DeviceCardBuilder';
import { DeviceCardProps } from '../DeviceCardProps';
import { useControlInitialization } from '../../../../hooks/controls/useControlInitialization';
import UnsupportedCodeStatus from './UnsupportedCodeStatus';
import { isUnsupportedNumericRawValue } from './unsupportedNumericValue';
import { useTranslation } from 'react-i18next';

interface SliderControlProps extends DeviceCardProps {
  label: React.ReactNode;
  labelId: string;
  labelPopover: React.ReactNode;
  labelPopoverId: string;
  labelUnits: React.ReactNode;
  step: number;
  min: number;
  max: number;
  marks?: { value: number; label?: React.ReactNode }[];
  shrinkLabel?: boolean;
}

const SliderControl = (props: SliderControlProps) => {
  const { t } = useTranslation();
  const {
    value: inputValue,
    setValue: setInputValue,
    unsupportedRawValue,
  } = useControlInitialization<number>(props, {
    min: props.min,
    max: props.max,
    parseValue: (v) => Number.parseFloat(v),
    isUnsupportedRawValue: isUnsupportedNumericRawValue,
  });

  const minVal = props.min ?? 0;
  const committedValue = typeof inputValue === 'number' && Number.isFinite(inputValue) ? inputValue : minVal;

  // Local value for the slider so the handle moves smoothly during drag.
  // The hook may re-run initialize() and overwrite its state; we only sync from
  // committed value when not dragging so the handle isn’t reset mid-drag.
  const [liveValue, setLiveValue] = useState(committedValue);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    if (!isDraggingRef.current) {
      setLiveValue(committedValue);
    }
  }, [committedValue]);

  const onChange = (_event: Event, newValue: number | number[]) => {
    isDraggingRef.current = true;
    setLiveValue(newValue as number);
  };

  const onChangeCommitted = (_event: Event | SyntheticEvent<Element, Event>, newValue: number | number[]) => {
    const numeric = newValue as number;
    isDraggingRef.current = false;
    setLiveValue(numeric);
    setInputValue(numeric);
    updateParameter(props, numeric.toString());
  };

  const displayValue = liveValue;
  const shrinkLabel = props.shrinkLabel ?? true;
  const isUnsupported = Boolean(unsupportedRawValue);

  return (
    <Box sx={{ width: '100%' }}>
      <InputLabel id={props.labelId} shrink={shrinkLabel} sx={{ position: 'static', mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography>{props.label}</Typography>
          <HelpPopover id={props.labelPopoverId} text={props.labelPopover} />
        </Stack>
      </InputLabel>
      <UnsupportedCodeStatus
        code={unsupportedRawValue}
        descriptionI18nKey="controlPanel.devices.unsupportedNumericParameter.description"
        descriptionDefaultValue="Unsupported parameter value: {{code}}"
      />
      {isUnsupported && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {t('controlPanel.devices.unsupportedNumericParameter.replacementLabel', {
            defaultValue: 'Choose a standard replacement value',
          })}
        </Typography>
      )}
      <Stack direction="row" spacing={2} alignItems="stretch" sx={{ width: '100%', maxWidth: '100%' }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
          <Slider
            aria-labelledby={props.labelId}
            onChange={onChange}
            onChangeCommitted={onChangeCommitted}
            value={displayValue}
            min={props.min}
            max={props.max}
            step={props.step}
            marks={props.marks}
            sx={{
              width: '100%',
              '& .MuiSlider-markLabel': {
                typography: 'body2',
                whiteSpace: 'nowrap',
              },
            }}
          />
        </Box>
        <Typography
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'flex-end',
            whiteSpace: 'nowrap',
            minWidth: '7ch',
            textAlign: 'right',
            flexShrink: 0,
            lineHeight: 1,
          }}
        >
          {displayValue}&nbsp;{props.labelUnits}
        </Typography>
      </Stack>
    </Box>
  );
};

export default SliderControl;
